import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/errors';
import type { FormTemplate } from '@/api/generated/client';

interface TemplateFormData {
  name: string;
  description: string;
  category: string;
}

/**
 * Hook to manage form templates
 * Provides CRUD operations for template management
 */
export function useTemplateManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    description: '',
    category: '',
  });

  // Fetch templates
  const { data: templates = [], isLoading } = useQuery<FormTemplate[]>({
    queryKey: ['form-templates', showArchived],
    queryFn: async () => {
      const response = await apiClient.formTemplates.listFormTemplates({ includeInactive: showArchived });
      return showArchived
        ? response.templates.filter((template) => !template.isActive)
        : response.templates;
    },
  });

  // Upload template mutation
  const uploadMutation = useMutation({
    mutationFn: async (data: { name: string; description: string; category: string; pdfFile: string }) => {
      const response = await apiClient.formTemplates.createFormTemplate({
        createFormTemplateRequest: data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-templates'] });
      toast({
        title: 'Template uploaded',
        description: 'Your template has been uploaded successfully.',
      });
      setIsUploadDialogOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: 'Upload failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete template mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.formTemplates.deleteFormTemplate({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-templates'] });
      toast({
        title: 'Template deleted',
        description: 'The template has been deleted successfully.',
      });
    },
    onError: async (error: Error) => {
      const message = await getApiErrorMessage(error);
      toast({
        title: 'Delete failed',
        description: message,
        variant: 'destructive',
      });
    },
  });

  const setTemplateActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      return await apiClient.formTemplates.updateFormTemplate({
        id,
        updateFormTemplateRequest: { isActive },
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['form-templates'] });
      toast({
        title: variables.isActive ? 'Template restored' : 'Template archived',
      });
    },
    onError: async (error: Error, variables) => {
      const message = await getApiErrorMessage(error);
      toast({
        title: variables.isActive ? 'Restore failed' : 'Archive failed',
        description: message,
        variant: 'destructive',
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
    });
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast({
        title: 'Invalid file type',
        description: 'Please select a PDF file.',
        variant: 'destructive',
      });
      // Clear invalid selection
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Enforce a 15 MB limit (base64 encoding adds ~33% overhead against the 20 MB backend limit)
    const MAX_FILE_SIZE = 15 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: 'File too large',
        description: 'PDF files must be under 15 MB.',
        variant: 'destructive',
      });
      // Clear invalid selection
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);

    // Auto-populate name if empty
    if (!formData.name) {
      const nameWithoutExt = file.name.replace(/\.pdf$/i, '');
      setFormData((prev) => ({ ...prev, name: nameWithoutExt }));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !formData.name.trim()) {
      toast({
        title: 'Missing information',
        description: 'Please provide a template name and select a PDF file.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Convert file to base64
      const base64 = await fileToBase64(selectedFile);

      uploadMutation.mutate({
        name: formData.name,
        description: formData.description,
        category: formData.category,
        pdfFile: base64,
      });
    } catch {
      toast({
        title: 'Upload failed',
        description: 'Failed to read the PDF file.',
        variant: 'destructive',
      });
    }
  };

  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);

  const handleDelete = (id: number, templateName: string) => {
    setDeleteTarget({ id, name: templateName });
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
  };

  const openUploadDialog = () => {
    setIsUploadDialogOpen(true);
  };

  const closeUploadDialog = () => {
    setIsUploadDialogOpen(false);
    resetForm();
  };

  return {
    // Data
    templates,
    isLoading,
    showArchived,
    setShowArchived,

    // Upload dialog state
    isUploadDialogOpen,
    openUploadDialog,
    closeUploadDialog,
    
    // Form state
    formData,
    setFormData,
    selectedFile,
    fileInputRef,

    // Operations
    handleFileSelect,
    handleUpload,
    handleDelete,
    confirmDelete,
    cancelDelete,
    deleteTarget,
    setTemplateActive: setTemplateActiveMutation.mutate,
    
    // Mutation states
    isUploading: uploadMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isUpdatingTemplateState: setTemplateActiveMutation.isPending,
  };
}

/**
 * Convert a File to base64 string
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error('Failed to read file as base64'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
